// SofaScore hidden API — sem chave, gratuita para uso pessoal
import { FootballEvent, FootballGame, GameStatus, GameStatistic, GameTimelineEvent } from '@/types';

const DIRECT_BASE_URL = 'https://api.sofascore.com/api/v1';
const PROXY_BASE_URL = process.env.EXPO_PUBLIC_API_BASE
  ? `${process.env.EXPO_PUBLIC_API_BASE}/sofa`
  : null;
const BASE_URL = PROXY_BASE_URL ?? DIRECT_BASE_URL;
const IMG_URL = DIRECT_BASE_URL;

const HEADERS = {
  'User-Agent': 'Mozilla/5.0 (Linux; Android 11; Mobile) AppleWebKit/537.36 Chrome/120.0.0.0 Mobile Safari/537.36',
  'Accept': 'application/json',
  'Accept-Language': 'pt-BR,pt;q=0.9',
  'Referer': 'https://www.sofascore.com/',
  'Origin': 'https://www.sofascore.com',
};

// ─── Tipos ──────────────────────────────────────────────────────────────────

export interface SofaTeam {
  id: number;
  name: string;
  shortName: string;
  nameCode: string;
}

export interface SofaScore {
  current?: number;
  period1?: number;
  period2?: number;
  overtime?: number;
  penalties?: number;
}

export interface SofaStatus {
  code: number;
  // 0=NS  6=1T  31=HT  7=2T  10=PRORR  100=FT  110=AET  120=PEN
  description: string;
  type: 'notstarted' | 'inprogress' | 'finished' | 'canceled' | 'postponed';
}

export interface SofaCategory {
  id: number;
  name: string;        // País/região ex: "Spain", "Brazil", "World"
  slug: string;
  flag?: string;       // slug do país ex: "spain", "brazil"
}

export interface SofaTournament {
  id: number;
  name: string;        // ex: "LaLiga", "Premier League"
  slug: string;
  category?: SofaCategory;
  uniqueTournament?: { id: number; name: string };
}

export interface SofaEvent {
  id: number;
  slug: string;
  tournament: SofaTournament;
  roundInfo?: { round: number; name?: string };
  status: SofaStatus;
  homeTeam: SofaTeam;
  awayTeam: SofaTeam;
  homeScore: SofaScore;
  awayScore: SofaScore;
  startTimestamp: number;
  time?: {
    played?: number;
    currentPeriodStartTimestamp?: number;
  };
}

export type SofaIncidentType =
  | 'goal'
  | 'card'
  | 'period'
  | 'substitution'
  | 'varDecision'
  | 'injuryTime';

export type SofaIncidentClass =
  | 'regular'
  | 'ownGoal'
  | 'penalty'
  | 'yellow'
  | 'red'
  | 'yellowRed';

export interface SofaIncident {
  id: number;
  time: number;
  addedTime?: number;
  type: SofaIncidentType;
  incidentClass?: SofaIncidentClass;
  // Para "period": "KO" | "HT" | "2nd" | "FT" | "ET" | "AP"
  text?: string;
  isHome?: boolean;
  homeScore?: number;
  awayScore?: number;
  player?: { id: number; name: string; shortName?: string };
  playerIn?: { id: number; name: string };
  playerOut?: { id: number; name: string };
}

export interface SofaStatisticItem {
  name: string;
  home?: string | number;
  away?: string | number;
  homeValue?: number;
  awayValue?: number;
}

export interface SofaStatisticGroup {
  statisticsItems?: SofaStatisticItem[];
  groups?: SofaStatisticGroup[];
}

// ─── Cache simples ───────────────────────────────────────────────────────────

const cache = new Map<string, { data: unknown; ts: number }>();

function cached<T>(key: string, ttl: number, fn: () => Promise<T>): Promise<T> {
  const e = cache.get(key);
  if (e && Date.now() - e.ts < ttl) return Promise.resolve(e.data as T);
  return fn().then((data) => { cache.set(key, { data, ts: Date.now() }); return data; });
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, { headers: HEADERS });
  if (!res.ok) throw new Error(`SofaScore ${res.status}: ${path}`);
  return res.json();
}

// ─── Futebol ─────────────────────────────────────────────────────────────────

export async function getSofaFootballByDate(date = new Date().toISOString().slice(0, 10)): Promise<SofaEvent[]> {
  return cached(`sofa:football:${date}`, 60_000, async () => {
    const json = await get<{ events: SofaEvent[] }>(`/sport/football/scheduled-events/${date}`);
    console.log(`[SofaScore] Futebol ${date}: ${json.events?.length ?? 0} partidas`);
    return json.events ?? [];
  });
}

export async function getSofaFootballToday(): Promise<SofaEvent[]> {
  return getSofaFootballByDate();
}

export async function getSofaFootballLive(): Promise<SofaEvent[]> {
  return cached('sofa:football:live', 15_000, async () => {
    const json = await get<{ events: SofaEvent[] }>('/sport/football/events/live');
    console.log(`[SofaScore] Futebol ao vivo: ${json.events?.length ?? 0} partidas`);
    return json.events ?? [];
  });
}

// ─── NBA ─────────────────────────────────────────────────────────────────────

export async function getSofaNBAToday(): Promise<SofaEvent[]> {
  const date = new Date().toISOString().slice(0, 10);
  return cached(`sofa:nba:${date}`, 60_000, async () => {
    const json = await get<{ events: SofaEvent[] }>(`/sport/basketball/scheduled-events/${date}`);
    return (json.events ?? []).filter((e) =>
      e.tournament?.name?.toLowerCase().includes('nba')
    );
  });
}

export async function getSofaNBALive(): Promise<SofaEvent[]> {
  return cached('sofa:nba:live', 15_000, async () => {
    const json = await get<{ events: SofaEvent[] }>('/sport/basketball/events/live');
    return (json.events ?? []).filter((e) =>
      e.tournament?.name?.toLowerCase().includes('nba')
    );
  });
}

// ─── NFL ─────────────────────────────────────────────────────────────────────

export async function getSofaNFLToday(): Promise<SofaEvent[]> {
  const date = new Date().toISOString().slice(0, 10);
  return cached(`sofa:nfl:${date}`, 60_000, async () => {
    const json = await get<{ events: SofaEvent[] }>(`/sport/american-football/scheduled-events/${date}`);
    return (json.events ?? []).filter((e) =>
      e.tournament?.name?.toLowerCase().includes('nfl')
    );
  });
}

export async function getSofaNFLLive(): Promise<SofaEvent[]> {
  return cached('sofa:nfl:live', 15_000, async () => {
    const json = await get<{ events: SofaEvent[] }>('/sport/american-football/events/live');
    return (json.events ?? []).filter((e) =>
      e.tournament?.name?.toLowerCase().includes('nfl')
    );
  });
}

// ─── Incidentes de uma partida ────────────────────────────────────────────────

export async function getSofaMatchIncidents(eventId: number): Promise<SofaIncident[]> {
  // Cache curto: 15s para não perder incidentes em tempo real
  return cached(`sofa:incidents:${eventId}`, 15_000, async () => {
    const json = await get<{ incidents: SofaIncident[] }>(`/event/${eventId}/incidents`);
    return json.incidents ?? [];
  });
}

export async function getSofaMatchStatistics(eventId: number): Promise<GameStatistic[]> {
  return cached(`sofa:statistics:${eventId}`, 30_000, async () => {
    const json = await get<{ statistics?: SofaStatisticGroup[] }>(`/event/${eventId}/statistics`);
    const groups = json.statistics ?? [];
    const items = groups.flatMap((group) => [
      ...(group.statisticsItems ?? []),
      ...((group.groups ?? []).flatMap((sub) => sub.statisticsItems ?? [])),
    ]);

    const wanted = [
      'Ball possession',
      'Total shots',
      'Shots on target',
      'Shots off target',
      'Corner kicks',
      'Fouls',
      'Yellow cards',
      'Red cards',
      'Offsides',
      'Goalkeeper saves',
      'Passes',
      'Accurate passes',
    ];

    const labels: Record<string, string> = {
      'Ball possession': 'Posse de bola',
      'Total shots': 'Finalizações',
      'Shots on target': 'Finalizações no alvo',
      'Shots off target': 'Finalizações para fora',
      'Corner kicks': 'Escanteios',
      Fouls: 'Faltas',
      'Yellow cards': 'Cartões amarelos',
      'Red cards': 'Cartões vermelhos',
      Offsides: 'Impedimentos',
      'Goalkeeper saves': 'Defesas',
      Passes: 'Passes',
      'Accurate passes': 'Passes certos',
    };

    const rows: GameStatistic[] = [];
    wanted.forEach((name) => {
      const item = items.find((stat) => stat.name === name);
      if (!item) return;
      rows.push({
        id: name,
        label: labels[name] ?? name,
        home: String(item.home ?? '-'),
        away: String(item.away ?? '-'),
        homeValue: item.homeValue,
        awayValue: item.awayValue,
      });
    });
    return rows;
  });
}

// ─── Mapeamento SofaScore → FootballGame ───────────────────────────────────────

// Bandeiras emoji por país (slug do SofaScore)
const COUNTRY_FLAGS: Record<string, string> = {
  world: '🌍', europe: '🇪🇺', 'south-america': '🌎', 'north-america': '🌎',
  brazil: '🇧🇷', argentina: '🇦🇷', spain: '🇪🇸', england: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', italy: '🇮🇹',
  germany: '🇩🇪', france: '🇫🇷', portugal: '🇵🇹', netherlands: '🇳🇱', usa: '🇺🇸',
  mexico: '🇲🇽', 'saudi-arabia': '🇸🇦', turkey: '🇹🇷', belgium: '🇧🇪', scotland: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  uruguay: '🇺🇾', colombia: '🇨🇴', chile: '🇨🇱', japan: '🇯🇵', 'south-korea': '🇰🇷',
};

function flagFor(category?: SofaCategory): string {
  if (!category) return '⚽';
  return COUNTRY_FLAGS[category.flag ?? category.slug] ?? '⚽';
}

export function sofaStatusToGameStatus(code: number): GameStatus {
  if (code === 31) return 'halftime';
  if ([6, 7, 10].includes(code)) return 'live';
  if ([100, 110, 120].includes(code)) return 'finished';
  if ([60, 70, 80].includes(code)) return 'postponed'; // adiado/cancelado/abandonado
  return 'scheduled';
}

// Minuto aproximado de jogos ao vivo de futebol
function liveMinute(event: SofaEvent): number | undefined {
  const code = event.status.code;
  const start = event.time?.currentPeriodStartTimestamp;
  if (!start) return undefined;
  const elapsed = Math.floor((Date.now() / 1000 - start) / 60) + 1;
  if (code === 6) return Math.min(elapsed, 45);           // 1º tempo
  if (code === 7) return Math.min(45 + elapsed, 90);      // 2º tempo
  if (code === 10) return Math.min(90 + elapsed, 120);    // prorrogação
  return undefined;
}

export function sofaTeamLogo(teamId: number): string {
  return `${IMG_URL}/team/${teamId}/image`;
}

export function sofaToFootballGame(event: SofaEvent): FootballGame {
  const cat = event.tournament.category;
  const uniqueId = event.tournament.uniqueTournament?.id;
  const status = sofaStatusToGameStatus(event.status.code);

  const game: FootballGame = {
    id: String(event.id),
    sport: 'futebol',
    league: {
      id: `sofa-${event.tournament.id}`,
      name: event.tournament.name,
      logo: uniqueId ? `${IMG_URL}/unique-tournament/${uniqueId}/image` : '',
      country: cat?.name ?? '',
      flag: flagFor(cat),
    },
    homeTeam: {
      id: String(event.homeTeam.id),
      name: event.homeTeam.name,
      shortName: event.homeTeam.shortName || event.homeTeam.nameCode || event.homeTeam.name.slice(0, 3).toUpperCase(),
      logo: sofaTeamLogo(event.homeTeam.id),
    },
    awayTeam: {
      id: String(event.awayTeam.id),
      name: event.awayTeam.name,
      shortName: event.awayTeam.shortName || event.awayTeam.nameCode || event.awayTeam.name.slice(0, 3).toUpperCase(),
      logo: sofaTeamLogo(event.awayTeam.id),
    },
    score: {
      home: event.homeScore.current ?? 0,
      away: event.awayScore.current ?? 0,
    },
    status,
    minute: status === 'live' ? liveMinute(event) : undefined,
    startTime: new Date(event.startTimestamp * 1000).toISOString(),
    events: [],
  };

  game.timeline = sofaStatusTimeline(game);
  return game;
}

function sofaStatusTimeline(game: FootballGame): GameTimelineEvent[] {
  const items: GameTimelineEvent[] = [];
  if (game.status !== 'scheduled') {
    items.push({ id: 'kickoff', side: 'center', type: 'game_start', title: 'Início da partida' });
  }
  if (game.status === 'halftime' || game.status === 'finished' || (game.minute ?? 0) > 45) {
    items.push({ id: 'halftime', side: 'center', type: 'halftime', title: 'Fim do primeiro tempo' });
  }
  if (game.status === 'live' && (game.minute ?? 0) > 45) {
    items.push({ id: 'second-half', side: 'center', type: 'period_start', title: 'Começou o segundo tempo' });
  }
  if (game.status === 'finished') {
    items.push({ id: 'fulltime', side: 'center', type: 'game_end', title: 'Fim de jogo' });
  }
  return items;
}

// ─── Timeline de incidentes (para a tela de detalhe do jogo) ────────────────────

export interface TimelineItem {
  id: number;
  minute: string;                       // "23'" | "45+2'" | ""
  emoji: string;
  text: string;                         // jogador ou nome do período
  detail?: string;                      // placar ou jogador que saiu
  side: 'home' | 'away' | 'center';     // center = período
}

export function sofaIncidentToTimeline(inc: SofaIncident): TimelineItem | null {
  const minute = inc.addedTime
    ? `${inc.time}+${inc.addedTime}'`
    : inc.time != null ? `${inc.time}'` : '';
  const player = inc.player?.shortName || inc.player?.name || '';
  const side: 'home' | 'away' | 'center' =
    inc.type === 'period' ? 'center' : inc.isHome ? 'home' : 'away';
  const score =
    inc.homeScore != null && inc.awayScore != null
      ? `${inc.homeScore} × ${inc.awayScore}`
      : undefined;

  if (inc.type === 'period') {
    const labels: Record<string, string> = {
      KO: 'Início do Jogo', HT: 'Intervalo', '2nd': 'Início 2º Tempo',
      FT: 'Fim de Jogo', ET: 'Prorrogação', AP: 'Pênaltis',
    };
    const label = labels[inc.text ?? ''] ?? inc.text ?? '';
    if (!label) return null;
    return { id: inc.id, minute: '', emoji: '⏱️', text: label, detail: score, side: 'center' };
  }

  if (inc.type === 'goal') {
    const cls = inc.incidentClass;
    const emoji = cls === 'ownGoal' ? '🥅' : '⚽';
    const tag = cls === 'penalty' ? ' (pênalti)' : cls === 'ownGoal' ? ' (contra)' : '';
    return { id: inc.id, minute, emoji, text: `${player}${tag}`, detail: score, side };
  }

  if (inc.type === 'card') {
    const cls = inc.incidentClass;
    if (cls === 'yellow') return { id: inc.id, minute, emoji: '🟨', text: player, side };
    if (cls === 'red' || cls === 'yellowRed') return { id: inc.id, minute, emoji: '🟥', text: player, side };
    return null;
  }

  if (inc.type === 'substitution') {
    const inName = inc.playerIn?.name ?? player;
    const outName = inc.playerOut?.name;
    return { id: inc.id, minute, emoji: '🔄', text: inName, detail: outName ? `↓ ${outName}` : undefined, side };
  }

  return null;
}

export function sofaIncidentToFootballEvent(inc: SofaIncident): FootballEvent | null {
  if (inc.type !== 'goal' && inc.type !== 'card' && inc.type !== 'substitution') {
    return null;
  }

  const team = inc.isHome ? 'home' : 'away';
  const player = inc.player?.shortName || inc.player?.name || inc.playerIn?.name;
  const minute = inc.time ?? 0;

  if (inc.type === 'goal') {
    const cls = inc.incidentClass;
    return {
      minute,
      type: cls === 'ownGoal' ? 'own_goal' : cls === 'penalty' ? 'penalty' : 'goal',
      team,
      player,
    };
  }

  if (inc.type === 'card') {
    const cls = inc.incidentClass;
    if (cls === 'yellow') {
      return { minute, type: 'yellow_card', team, player };
    }
    if (cls === 'red' || cls === 'yellowRed') {
      return { minute, type: 'red_card', team, player };
    }
    return null;
  }

  return {
    minute,
    type: 'substitution',
    team,
    player: inc.playerIn?.name,
    assist: inc.playerOut?.name,
  };
}

// ─── Cobertura completa: TODOS os jogos (como uma casa de apostas mostra) ───────

// Todos os jogos de futebol de hoje, do mundo inteiro, de qualquer liga
export async function getSofaAllFootballToday(): Promise<FootballGame[]> {
  const events = await getSofaFootballToday();
  return events.map(sofaToFootballGame);
}

export async function getSofaAllFootballByDate(date: string): Promise<FootballGame[]> {
  const events = await getSofaFootballByDate(date);
  return events.map(sofaToFootballGame);
}

// Todos os jogos de futebol AO VIVO agora, do mundo inteiro
export async function getSofaAllFootballLive(): Promise<FootballGame[]> {
  const events = await getSofaFootballLive();
  return events.map(sofaToFootballGame);
}

// ─── Status helpers ───────────────────────────────────────────────────────────

export function isLiveStatus(code: number): boolean {
  return [6, 7, 10, 31].includes(code);
}

export function isFinishedStatus(code: number): boolean {
  return [100, 110, 120].includes(code);
}

export function statusLabel(code: number): string {
  const map: Record<number, string> = {
    0: 'Não iniciado',
    6: '1º Tempo',
    31: 'Intervalo',
    7: '2º Tempo',
    10: 'Prorrogação',
    100: 'Encerrado',
    110: 'Encerrado (Prorr.)',
    120: 'Encerrado (Pen.)',
  };
  return map[code] ?? 'Desconhecido';
}
